import { isInstagramId } from '@/lib/channel';
import type { DeliveryDispatchDto } from '@/lib/deliveries-client';
import type {
  DeliveryDispatchStatus,
  DeliveryPaymentMode,
  DeliveryProviderSupports,
} from '@/lib/delivery-providers-client';
import type { PaymentPreference } from '@/lib/order-board-client';

/**
 * The "Solicitar repartidor" step of "Nuevo envío" (spec D8). Amounts are kept as the strings the
 * inputs hold, so a half-typed "12," is not rewritten under the operator's cursor.
 */
export interface DeliveryAmounts {
  orderAmount: string;
  deliveryFee: string;
  /** "Pedido gratis": both amounts are 0 and the inputs are locked. */
  free: boolean;
}

/** A typed amount as a number: blank, negative or garbage reads as 0 (both may legitimately be 0). */
export function parseAmount(value: string): number {
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/** Ticking "Pedido gratis" zeroes both amounts; unticking leaves them at 0 for the operator to fill. */
export function applyFreeOrder(amounts: DeliveryAmounts, free: boolean): DeliveryAmounts {
  return free ? { orderAmount: '0', deliveryFee: '0', free: true } : { ...amounts, free: false };
}

/** "Total informado al repartidor": what the rider is told the customer owes (or paid). */
export function riderTotal(amounts: DeliveryAmounts): number {
  if (amounts.free) return 0;
  return Math.round((parseAmount(amounts.orderAmount) + parseAmount(amounts.deliveryFee)) * 100) / 100;
}

/**
 * The payment modes the operator may pick. `terminal` (the rider carries a card POS) only exists
 * when the brand has its own fleet on the provider (D7).
 */
export function availablePaymentModes(myFleet: boolean): DeliveryPaymentMode[] {
  return myFleet ? ['cash', 'online', 'terminal'] : ['cash', 'online'];
}

/**
 * The payment mode a standalone delivery starts on, and follows while the operator has not picked
 * one: `cash` when there is something to collect, `online` (nothing to collect) when the total is 0.
 * Once they picked one (`touched`) it stays — unless it stopped being offered.
 */
export function standalonePaymentMode(
  total: number,
  current: DeliveryPaymentMode,
  touched: boolean,
  myFleet: boolean,
): DeliveryPaymentMode {
  if (touched && availablePaymentModes(myFleet).includes(current)) {
    // Nothing left to collect: a cash or terminal run would send the rider to charge 0.
    if (total === 0 && current !== 'online') return 'online';
    return current;
  }
  return total > 0 ? 'cash' : 'online';
}

/**
 * The payment mode a rider for an ORDER defaults to, from how the customer pays (spec §4.2): cash →
 * the rider collects; "Mandar POS" → the rider brings the terminal (needs own fleet, else the
 * server refuses with 409 `DELIVERY_PAYMENT_METHOD_UNSUPPORTED`); anything else was paid already.
 */
export function orderPaymentMode(preference: PaymentPreference | null | undefined): DeliveryPaymentMode {
  if (preference === 'cash') return 'cash';
  if (preference === 'send_pos_for_card') return 'terminal';
  return 'online';
}

/** The order cannot travel as it pays: "Mandar POS" without own fleet (D7). */
export function orderPaymentBlocked(preference: PaymentPreference | null | undefined, myFleet: boolean): boolean {
  return preference === 'send_pos_for_card' && !myFleet;
}

/** The statuses the "Envío" timeline walks through, in order — what a healthy run looks like. */
export const DISPATCH_TIMELINE: DeliveryDispatchStatus[] = [
  'requested',
  'on_hold',
  'searching',
  'assigned',
  'at_pickup',
  'on_the_way',
  'at_dropoff',
  'delivered',
];

/** The longest a booking may wait before looking for a rider, in minutes (UI and API agree). */
export const MAX_PREP_TIME_MINUTES = 240;

/** A prep time as typed → whole minutes in [0, MAX_PREP_TIME_MINUTES]; blank or garbage is 0. */
export function clampPrepMinutes(value: string | number): number {
  const parsed = Math.round(Number(value));
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.min(parsed, MAX_PREP_TIME_MINUTES);
}

/**
 * When a held booking goes out: its prep time after the request, less the courier lead of a provider
 * Menteo holds for (spec L4: the courier's trip overlaps the end of the preparation). Lead 0 — a
 * provider that holds itself — is the prep time as is.
 */
export function bookingStartsAt(requestedAt: string, prepTimeMinutes: number, courierLeadMinutes: number): Date {
  const delayMinutes = Math.max(0, prepTimeMinutes - courierLeadMinutes);
  return new Date(new Date(requestedAt).getTime() + delayMinutes * 60_000);
}

/**
 * When a booking Menteo holds locally goes out: the API's `bookAfter` (null for a provider that holds
 * itself, or a booking sent at once). An older API sends no field: then the prep time less the courier
 * lead, for a provider that has one — null otherwise.
 */
export function localHoldUntil(
  dispatch: Pick<DeliveryDispatchDto, 'bookAfter' | 'requestedAt' | 'prepTimeMinutes'>,
  courierLeadMinutes: number,
): Date | null {
  if (dispatch.bookAfter !== undefined) return dispatch.bookAfter ? new Date(dispatch.bookAfter) : null;
  return courierLeadMinutes > 0
    ? bookingStartsAt(dispatch.requestedAt, dispatch.prepTimeMinutes, courierLeadMinutes)
    : null;
}

/**
 * The request will be held by Menteo rather than sent now: a provider without `HOLD` and a prep time
 * longer than its courier lead (spec L4). Such a booking re-checks coverage when the hold ends.
 */
export function isHeldForLater(
  prepTimeMinutes: number,
  courierLeadMinutes: number,
  supports: Pick<DeliveryProviderSupports, 'hold'>,
): boolean {
  return !supports.hold && prepTimeMinutes > courierLeadMinutes;
}

/** A distance for coverage copy, in the UI language: "2,4" in Spanish, "2.4" in English, "3" for 3. */
export function formatKm(value: number | string, language: string): string {
  const parsed = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(parsed)) return '—';
  return new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(parsed);
}

/** A phone as digits with an optional leading +, for the lookup and the request body. */
export function normalizePhone(value: string): string {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, '');
  return trimmed.startsWith('+') ? `+${digits}` : digits;
}

/** Enough digits to be worth a customer lookup (and to be a phone at all). */
export function isLookupablePhone(value: string): boolean {
  return normalizePhone(value).replace('+', '').length >= 8;
}

/**
 * Whether an order's customer phone is one the rider can call. Empty is not, and neither is an
 * Instagram customer's `ig:<id>`, which stands in for the phone — "Pedir repartidor" asks for one then.
 */
export function isUsableCustomerPhone(value: string | null | undefined): boolean {
  const phone = value?.trim() ?? '';
  return phone !== '' && !isInstagramId(phone);
}
